<template>
  <div>
    <Layout :containers="containers">
      <template v-slot:header>
        <DefaultHeroBar
          :hasQuickStartCallout="false"
          :heroTitle="'Plan Ahead: Calculate Your Estate Costs and Taxes'"
          :heroDescription="'Use our free calculators to estimate capital gains tax and estate costs in minutes.'"
          :section="'calculator'"
        />
      </template>
      <template v-slot:tabbed-content>
        <Breadcrumb />
        <TitleRow
          v-if="whitelabel.omDesign"
          :title="computedTitle"
          :description="`Along with the heartache of death comes the additional burden of legal and administration fees. 
          It is important to plan for these costs so it does not cause stress for your loved ones at a time of grieving.`"
        />
        <TabbedContentComponent
          v-if="!hideTabs"
          :tabTitles="[
            'Estate Administration Cost Calculator',
            'Capital Gains Tax Calculator',
          ]"
          :activeTab="currentActiveTab"
          @tab-changed="handleTabChange"
        >
          <template v-slot:tab-zero>
            <EstateCostCalculator
              v-if="whitelabel.omDesign"
              ref="estateCostCalculator"
            />
            <EstateAdministrationCostCalculator
              v-else
              ref="estateAdministrationCostCalculator"
            />
          </template>
          <template v-slot:tab-one>
            <CapitalTaxGainsCalculator ref="capitalTaxGainsCalculator" />
          </template>
        </TabbedContentComponent>
        <div v-else>
          <div v-if="whitelabel.omDesign">
            <EstateCostCalculator
              v-if="activeTab === 0"
              ref="estateCostCalculator"
            />
            <CapitalTaxGainsCalculator
              v-if="activeTab === 1"
              ref="capitalTaxGainsCalculator"
            />
          </div>
          <div v-else>
            <EstateAdministrationCostCalculator
              v-if="activeTab === 0"
              ref="estateAdministrationCostCalculator"
            />
            <CapitalTaxGainsCalculator
              v-if="activeTab === 1"
              ref="capitalTaxGainsCalculator"
            />
          </div>
        </div>
      </template>
      <template v-slot:footer>
        <CalculatorFooter
          @requestACall="requestACall"
          v-if="whitelabel.brand === 'oldmutual'"
        />
      </template>
    </Layout>
  </div>
</template>
<script>
  import Layout from '../../common/layouts/Layout.vue';
  import DefaultHeroBar from '../../common/DefaultHeroBar.vue';
  import Breadcrumb from '../../common/Breadcrumb.vue';
  import TabbedContentComponent from '../../common/TabbedContentComponent.vue';
  import EstateCostCalculator from './estate_cost_calculator/EstateCostCalculator.vue';
  import TitleRow from '../../common/TitleRow.vue';
  import CapitalTaxGainsCalculator from './capital_gains_tax/CapitalTaxGainsCalculator.vue';
  import CalculatorFooter from './CalculatorFooter.vue';
  import EstateAdministrationCostCalculator from '../EstateCostCalculator/EstateAdministrationCostCalculator.vue';
  export default {
    name: 'CalculatorsContainer',
    components: {
      DefaultHeroBar,
      Layout,
      Breadcrumb,
      TabbedContentComponent,
      TitleRow,
      EstateCostCalculator,
      CapitalTaxGainsCalculator,
      CalculatorFooter,
      EstateAdministrationCostCalculator,
    },
    props: {
      activeTab: {
        type: Number,
        default: 0,
      },
      hideTabs: {
        type: Boolean,
        default: false,
      },
      scrollToCalculator: {
        type: Boolean,
        default: false,
      },
    },
    metaInfo() {
      return {
        title: this.whitelabel.metaData.calculator.title,
        meta: [
          {
            name: 'description',
            content: this.whitelabel.metaData.calculator.description,
          },
          {
            property: 'og:title',
            content: this.whitelabel.metaData.calculator.ogTitle,
          },
          {
            property: 'og:image',
            content: this.whitelabel.metaData.calculator.ogImage,
          },
          {
            property: 'og:type',
            content: this.whitelabel.metaData.calculator.ogType,
          },
          {
            property: 'og:url',
            content: this.whitelabel.metaData.calculator.ogUrl,
          },
          {
            property: 'og:description',
            content: this.whitelabel.metaData.calculator.description,
          },
          {
            property: 'og:site_name',
            content: this.whitelabel.metaData.calculator.ogSiteName,
          },
        ],
      };
    },
    data() {
      return {
        successMessage: null,
        errorMessage: null,
        showSuccess: false,
        showError: false,
        showAlert: false,
        alertMessage: null,
        showRequestACallDialog: false,
        localActiveTab: this.activeTab,
      };
    },
    computed: {
      containers() {
        return [
          {
            name: 'header',
            background: 'countryPattern',
            class: 'hero-banner',
          },
          {
            name: 'tabbed-content',
            background: 'countryPattern',
            fadedRow: true,
            accents: ['green-accent-1'],
            class: 'tabbed-content',
          },
          {
            name: 'footer',
          },
        ];
      },
      computedTitle() {
        if (this.hideTabs) {
          return '';
        } else {
          return 'Our Calculators';
        }
      },
      currentActiveTab() {
        return this.localActiveTab;
      },
    },
    watch: {
      activeTab: {
        handler(newVal) {
          // Update local state when prop changes
          this.localActiveTab = newVal;
        },
        immediate: true,
      },
    },
    mounted() {
      if (this.scrollToCalculator) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.scrollToCalculatorForRoute();
          }, 100);
        });
      }
    },
    methods: {
      requestACall() {
        this.showRequestACallDialog = true;
      },
      handleTabChange(tabIndex) {
        this.localActiveTab = tabIndex;
        this.$emit('update:activeTab', tabIndex);
      },
      scrollToCalculatorForRoute() {
        if (this.activeTab === 1) {
          const calculatorContainer = document.querySelector(
            '.calculator-container'
          );
          if (calculatorContainer) {
            calculatorContainer.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }
        } else if (this.activeTab === 0) {
          const calculatorContainer = document.querySelector(
            '.calculator-container'
          );
          if (calculatorContainer) {
            calculatorContainer.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }
        }
      },
    },
  };
</script>
